<template>
  <div
    :class="[
      $style.base,
      {
        [$style.disabled]: disabled
      }
    ]"
  >
    <label :class="$style.control">
      <input
        :checked="modelValue"
        :class="$style.input"
        type="checkbox"
        @change="toggle"
        v-test="'_base-toggle-input'"
      >
      <span
        :class="[$style.slider, { [$style.isChecked]: modelValue }]"
        v-test="'_base-toggle-slider'"
      />
    </label>
    <div
      v-if="label || description"
      :class="$style.text"
    >
      <BaseText
        v-if="label"
        :bold="!!description"
        :color="disabled ? 'placeholder' : 'default'"
        v-test="'_base-toggle-label'"
      >
        {{ label }}
      </BaseText>
      <BaseText
        v-if="description"
        :color="disabled ? 'placeholder' : 'secondary'"
        v-test="'_base-toggle-description'"
      >
        {{ description }}
      </BaseText>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  modelValue: boolean;
  label?: string;
  description?: string;
  disabled?: boolean;
}>();

const emit = defineEmits(['update:modelValue']);

const toggle = () => {
  if (!props.disabled) {
    emit('update:modelValue', !props.modelValue);
  }
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &.disabled {
    pointer-events: none;
  }
}

.text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  line-height: 20px;
}

// Centres the 26px track on the 20px label line
.control {
  flex-shrink: 0;
  position: relative;
  width: 42px;
  height: 26px;
  margin-top: -3px;

  .base:not(:has(.text)) & {
    margin-top: 0;
  }
}

.input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border-strong);
  transition: 0.3s;
  border-radius: 34px;

  &::before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 4px;
    bottom: 4px;
    background-color: var(--text-on-action-primary);
    transition: 0.3s;
    border-radius: 50%;
  }

  &:hover {
    background-color: var(--neutral-base);
  }

  .input:focus-visible + & {
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  .base.disabled & {
    background-color: var(--neutral-soft);

    &::before {
      background-color: var(--neutral-softer);
    }
  }
}

.isChecked {
  background-color: var(--selection);

  &:hover {
    background-color: var(--selection-hover);
  }

  &::before {
    transform: translateX(16px);
  }

  .base.disabled & {
    background-color: var(--neutral-base);
  }
}
</style>
